import { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/Tabs';
import { Button } from '@/shared/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/Dialog';
import { Input } from '@/shared/components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/ui/Table';
import { Badge } from '@/shared/components/ui/Badge';
import { formatINR } from '@/shared/utils/currency';
import { Plus } from 'lucide-react';
import { useQuotations } from '@/features/quotations/hooks/useQuotations';

export function DataPage() {
  const { data: quotations = [], isLoading: isLoadingQuotations } = useQuotations();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('quotations');

  // Manual Invoice Form State
  const [customerName, setCustomerName] = useState('');
  const [lines, setLines] = useState([{ name: '', quantity: 1, rate: 0, taxRate: 0 }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const confirmedQuotations = (quotations || []).filter((q: any) => q.status?.toLowerCase() === 'confirmed');

  const fetchInvoices = async () => {
    setIsLoadingInvoices(true);
    try {
      const res = await fetch('/api/accounting/sales/invoices');
      const data = await res.json();
      setInvoices(data.data?.items || []);
    } catch (err) {
      console.error('Failed to fetch invoices', err);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleCreateManualInvoice = async () => {
    if (!customerName) return alert('Customer Name is required');
    if (!lines[0].name) return alert('At least one item is required');

    setIsSubmitting(true);
    try {
      const payload = {
        customerId: customerName, // Quick hack for manual string names
        customerName: customerName,
        lines: lines.map(l => ({
          itemName: l.name,
          quantity: l.quantity,
          unitPriceMinor: Math.round(l.rate * 100),
          taxRate: l.taxRate,
          discountMinor: 0
        })),
        notes: "Manually generated invoice"
      };

      const res = await fetch('/api/accounting/sales/invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const resData = await res.json();
      
      if (!res.ok) {
        throw new Error(resData.error || 'Failed to create invoice');
      }

      setIsManualModalOpen(false);
      setCustomerName('');
      setLines([{ name: '', quantity: 1, rate: 0, taxRate: 0 }]);
      fetchInvoices();
    } catch (err: any) {
      alert(err.message || 'Failed to create invoice');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateLine = (index: number, field: string, value: any) => {
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setLines(newLines);
  };

  const addLine = () => setLines([...lines, { name: '', quantity: 1, rate: 0, taxRate: 0 }]);
  const removeLine = (index: number) => setLines(lines.filter((_, i) => i !== index));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Data</h1>
        <p className="text-muted-foreground">Manage Quotations and Invoices.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="quotations">Quotations</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
          </TabsList>
          {activeTab === 'invoices' && (
            <Button onClick={() => setIsManualModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> Manual Invoice
            </Button>
          )}
        </div>

        <TabsContent value="quotations" className="mt-4">
          <div className="bg-white dark:bg-slate-900 border rounded-xl overflow-hidden shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document No</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingQuotations ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8">Loading...</TableCell></TableRow>
                ) : confirmedQuotations.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8">No confirmed quotations found.</TableCell></TableRow>
                ) : (
                  confirmedQuotations.map((q: any) => (
                    <TableRow key={q._id}>
                      <TableCell className="font-medium text-blue-600 dark:text-blue-400">
                        {q.documentNumber || q._id.slice(-6)}
                      </TableCell>
                      <TableCell>{new Date(q.date).toLocaleDateString()}</TableCell>
                      <TableCell>{q.customerName || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{q.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatINR(q.grandTotal)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <div className="bg-white dark:bg-slate-900 border rounded-xl overflow-hidden shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice No</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingInvoices ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8">Loading...</TableCell></TableRow>
                ) : invoices.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8">No invoices found.</TableCell></TableRow>
                ) : (
                  invoices.map((inv: any) => (
                    <TableRow key={inv._id}>
                      <TableCell className="font-medium text-blue-600 dark:text-blue-400">
                        {inv.documentNumber || inv._id.slice(-6)}
                      </TableCell>
                      <TableCell>{new Date(inv.date).toLocaleDateString()}</TableCell>
                      <TableCell>{inv.customerName || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{inv.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatINR(inv.grandTotalMinor / 100)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={isManualModalOpen} onOpenChange={setIsManualModalOpen}>
        <DialogContent className="sm:max-w-[700px] bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle>Manual Invoice Generation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-xs font-semibold mb-1 block">Select Client from Quotations</label>
              <select 
                value={customerName} 
                onChange={(e) => setCustomerName(e.target.value)} 
                className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded px-3 py-2 text-sm mb-2"
              >
                <option value="">-- Select Client --</option>
                {Array.from(new Set(confirmedQuotations.map((q: any) => q.customerName).filter(Boolean))).map((client: any) => (
                  <option key={client} value={client}>{client}</option>
                ))}
              </select>

              <label className="text-xs font-semibold mb-1 block">Or Enter Client Name Manually</label>
              <Input 
                value={customerName} 
                onChange={(e) => setCustomerName(e.target.value)} 
                placeholder="Enter client name..."
              />
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-xs font-semibold block">Line Items</label>
                <Button size="sm" variant="outline" onClick={addLine}>Add Item</Button>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {lines.map((line, idx) => (
                  <div key={idx} className="flex gap-2 items-center bg-slate-50 dark:bg-slate-800 p-2 rounded border border-slate-200 dark:border-slate-700">
                    <Input 
                      placeholder="Item name" 
                      value={line.name} 
                      onChange={e => updateLine(idx, 'name', e.target.value)} 
                      className="flex-1"
                    />
                    <Input 
                      type="number" 
                      placeholder="Qty" 
                      value={line.quantity} 
                      onChange={e => updateLine(idx, 'quantity', Number(e.target.value))} 
                      className="w-20"
                    />
                    <Input 
                      type="number" 
                      placeholder="Rate" 
                      value={line.rate} 
                      onChange={e => updateLine(idx, 'rate', Number(e.target.value))} 
                      className="w-24"
                    />
                    <Input 
                      type="number" 
                      placeholder="Tax %" 
                      value={line.taxRate} 
                      onChange={e => updateLine(idx, 'taxRate', Number(e.target.value))} 
                      className="w-20"
                    />
                    <Button variant="ghost" size="sm" onClick={() => removeLine(idx)} disabled={lines.length === 1}>
                      X
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManualModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateManualInvoice} loading={isSubmitting}>Generate Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DataPage;
